import React from "react";
import BoolField from "../FormFields/BoolField";
import ButtonField from "../FormFields/ButtonField";
import CalendarField from "../FormFields/CalendarField";
import CheckboxField from "../FormFields/CheckboxField";
import DictionaryField from "../FormFields/DictionaryField";
import FileField from "../FormFields/FileField";
import ListTableField from "../FormFields/ListTableField";
import QuestionnaireTableField from "../FormFields/QuestionnaireTableField";
import RadioField from "../FormFields/RadioField";
import SectionField from "../FormFields/SectionField";
import SignatureField from "../FormFields/SignatureField/SignatureField";
import SqlField from "../FormFields/SqlField";
import TableField from "../FormFields/TableField";
import TextareaField from "../FormFields/TextareaField";
import TextField from "../FormFields/TextField";

export const renderField = (element, propsForAllFields, fieldInModal?) => {
   switch (element?.type) {
      case "text":
         return <TextField {...propsForAllFields(element)} />;
      case "section":
         return <SectionField {...propsForAllFields(element)} />;
      case "sql":
         return <SqlField {...propsForAllFields(element)} />;
      case "textarea":
         return <TextareaField {...propsForAllFields(element)} />;
      case "select":
         return <DictionaryField fieldInModal={fieldInModal} {...propsForAllFields(element)} />;
      case "calendar":
         return <CalendarField {...propsForAllFields(element)} />;
      case "checkbox":
         return <CheckboxField {...propsForAllFields(element)} />;
      case "file":
         return element.tech_name.includes("_signature") ? (
            <SignatureField {...propsForAllFields(element)} />
         ) : (
            <FileField {...propsForAllFields(element)} />
         );
      case "table":
         return <TableField {...propsForAllFields(element)} />;
      case "listTable":
         return element?.questionnaireTable === "1" ? (
            <QuestionnaireTableField {...propsForAllFields(element)} />
         ) : (
            <ListTableField {...propsForAllFields(element)} />
         );
      case "radio":
         return <RadioField {...propsForAllFields(element)} />;
      case "bool":
         return <BoolField {...propsForAllFields(element)} />;
      case "button":
         return <ButtonField {...propsForAllFields(element)} />;
      default:
         return null;
   }
};

export const prepareBlankRequiredFields = (fields, elements, intl) => {
   const getFieldsByValue = (obj, value) => {
      return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v === value));
   };

   const missingFields = getFieldsByValue(fields, intl.formatMessage({ id: "field.required" }));

   const missingKeys = Object.keys(missingFields);

   const filteredElements = elements.filter((el) => missingKeys.includes(el.tech_name));

   if (filteredElements?.length > 0) {
      return filteredElements;
   }
};

export const getDependentFieldValue = (field, form, elements) => {
   if (!form) return;

   let fieldValue = form.getFieldValue(field.techname);
   const fieldData = Object.values(elements).find((el) => el.tech_name === field.techname);
   if (fieldData?.type === "select") {
      fieldValue = Object.entries(fieldData.options).find(
         ([key, label]) => label === fieldValue,
      )?.[0];
   }

   if (typeof fieldValue === "boolean") {
      return fieldValue ? "1" : "0";
   }
   return fieldValue || "";
};

export const resolveDependentFields = (
   arrayOfFields,
   dependencyType,
   operation,
   form,
   elements,
) => {
   let returnValue = false;

   for (const field of arrayOfFields) {
      field.techname = field.tech_name || field.techname;
      let dependentFieldValue = getDependentFieldValue(field, form, elements);

      if (
         isNaN(+dependentFieldValue) && isNaN(+field.value)
            ? field.value === dependentFieldValue
            : +field.value === +dependentFieldValue
      ) {
         returnValue = field[dependencyType] === operation;
      } else {
         return field[dependencyType] !== operation;
      }
   }
   return returnValue;
};

export const checkIfDisabled = (uipEdyt, element, isReadOnly, form, elements) => {
   const { editabilityControls } = element;
   if (editabilityControls && editabilityControls.length > 0) {
      return !resolveDependentFields(editabilityControls, "editable", "editable", form, elements);
   }
   if (uipEdyt === "0" || (uipEdyt === "1" && isReadOnly === true)) {
      return true;
   } else return false;
};

export const checkIfRequired = (
   element,
   form,
   elements,
   areOnlyRequiredFieldsVisible,
   alertsFields,
) => {
   try {
      const { showHideFields, obow } = element;

      const isNotAlwaysRequired = showHideFields.some(
         (filter) => filter.required === "not_always_required",
      );

      const requiredDependencies = JSON.parse(element.object?.UIP_REQUIRED_CONTROL || "[]");
      const isAnyDependency =
         Array.isArray(requiredDependencies) && requiredDependencies.length > 0;
      let dependencyRequired = isAnyDependency;
      isAnyDependency &&
         requiredDependencies.forEach((dependency) => {
            if (dependency.state === "required") {
               if (dependency.value != form.getFieldValue(dependency.techname)) {
                  dependencyRequired = false;
               }
            }
         });
      dependencyRequired = dependencyRequired || obow === "1";

      if (!isNotAlwaysRequired) return dependencyRequired;
      return (
         checkIfVisible(element, areOnlyRequiredFieldsVisible, alertsFields, form, elements) &&
         (element.object?.UIP_OBOW === "1" || dependencyRequired)
      );
   } catch {
      return false;
   }
};

export const checkIfVisible = (
   element,
   areOnlyRequiredFieldsVisible,
   alertsFields,
   form,
   elements,
) => {
   const { hidden, showHideFields, obow, value, object, type, tech_name } = element;

   if (areOnlyRequiredFieldsVisible) {
      if (showHideFields && showHideFields.length > 0) {
         return (
            (resolveDependentFields(showHideFields, "state", "show", form, elements) &&
               object.UIP_EDYT === "1" &&
               value === "") ||
            (type === "bool" && value === "1")
         );
      }

      if (obow === "1" && value === "" && object.UIP_EDYT === "1") {
         return true;
      }

      if (type === "listTable") {
         return true;
      }

      if (type === "textarea" && obow !== "1" && object.UIP_EDYT === "1" && value !== "") {
         return true;
      }

      if (alertsFields.has(tech_name) && hidden !== "1") {
         return true;
      }

      return false;
   }

   if (showHideFields && showHideFields.length > 0) {
      return resolveDependentFields(showHideFields, "state", "show", form, elements);
   }

   if (hidden === "1") return false;

   return true;
};

export const shouldHideTab = (tab, areOnlyRequiredFieldsVisible, fieldsInfo, form) => {
   if (!areOnlyRequiredFieldsVisible) return false;

   const tabElements = Object.values(tab.elements || {});
   if (tabElements.length === 0) return true;

   const alertsFields = new Set(
      Object.entries(fieldsInfo ?? {})
         .filter(([_, val]) => val && val.length > 0)
         .map(([techname]) => techname),
   );

   return !tabElements.some((element) =>
      checkIfVisible(element, areOnlyRequiredFieldsVisible, alertsFields, form, tabElements),
   );
};
